import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { snapPanelSide, type GamesPanelSide } from "../constants";
import { useGamesPortalStore } from "../store/games-portal-store";

const MOVE_THRESHOLD = 8;

/**
 * Başlıktan tutup sürükleyince paneli sağ / sol kenara manyetik yapıştırır.
 */
export const useGamesPanelMove = (enabled: boolean) => {
  const panelSide = useGamesPortalStore((state) => state.panelSide);
  const setPanelSide = useGamesPortalStore((state) => state.setPanelSide);
  const panelWidth = useGamesPortalStore((state) => state.panelWidth);

  const [isMoving, setIsMoving] = useState(false);
  const [hoverSide, setHoverSide] = useState<GamesPanelSide | null>(null);
  const [previewLeft, setPreviewLeft] = useState<number | null>(null);
  const dragRef = useRef<{
    startX: number;
    offsetX: number;
    moved: boolean;
  } | null>(null);
  const armedRef = useRef(false);

  const endDrag = useCallback(
    (clientX: number) => {
      const drag = dragRef.current;
      const moved = Boolean(drag?.moved);
      dragRef.current = null;
      armedRef.current = false;
      setIsMoving(false);
      setHoverSide(null);
      setPreviewLeft(null);
      document.body.style.removeProperty("cursor");
      document.body.style.removeProperty("user-select");
      if (moved) {
        setPanelSide(snapPanelSide(clientX, window.innerWidth));
      }
    },
    [setPanelSide]
  );

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || !armedRef.current) return;
      if (!drag.moved && Math.abs(event.clientX - drag.startX) < MOVE_THRESHOLD) {
        return;
      }
      drag.moved = true;
      if (!isMoving) setIsMoving(true);
      const maxLeft = Math.max(16, window.innerWidth - panelWidth - 16);
      const nextLeft = Math.min(
        maxLeft,
        Math.max(16, event.clientX - drag.offsetX)
      );
      setPreviewLeft(nextLeft);
      setHoverSide(snapPanelSide(event.clientX, window.innerWidth));
    };

    const onUp = (event: PointerEvent) => {
      if (!armedRef.current && !dragRef.current) return;
      endDrag(event.clientX);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [endDrag, isMoving, panelSide, panelWidth]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!enabled || event.button !== 0) return;
      if ((event.target as HTMLElement).closest("button")) return;
      event.preventDefault();
      const rect = event.currentTarget.getBoundingClientRect();
      dragRef.current = {
        startX: event.clientX,
        offsetX: event.clientX - rect.left,
        moved: false,
      };
      armedRef.current = true;
      document.body.style.cursor = "grabbing";
      document.body.style.userSelect = "none";
    },
    [enabled]
  );

  return {
    isMoving,
    hoverSide,
    previewLeft,
    handleProps: {
      onPointerDown,
    },
  };
};
